import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Title,
  Filler,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import { Line } from "react-chartjs-2";
import React, { useRef, useMemo } from "react";
import { useBot } from "../../context/botContext";
import moment from "moment";

// Register Chart.js modules
ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Title,
  Filler,
  zoomPlugin
);

// Parse timestamp like "2025-06-07 13:00:41" to Date and return "YYYY-M"
const getMonthKey = (dateStr) => {
  const isoDateStr = dateStr.replace(" ", "T");
  const date = new Date(isoDateStr);
  return `${date.getFullYear()}-${date.getMonth() + 1}`;
};

export const ChartBot = () => {
  const { dataChart, botDetail, loadingBotDetail } = useBot();
  // const { botDetail, loadingBotDetail } = useBot();
  const chartRef = useRef();

  const currentMonthKey = getMonthKey(new Date().toISOString());

  // Filter dataChart by current month
  const filteredChartData = (dataChart || []).filter(
    (item) => getMonthKey(item.timestamp) === currentMonthKey
  );

  // const filteredChartData = dataChart;

  const values = filteredChartData.map((d) => d.value);
  const minY = values.length > 0 ? Math.floor(Math.min(...values) * 1.15) : 0;
  const maxY = values.length > 0 ? Math.ceil(Math.max(...values) * 1.15) : 100;

  const chartData = useMemo(() => {
    const labels = filteredChartData.map((item) =>
      moment(item.timestamp, "YYYY-MM-DD HH:mm:ss").format("MMM DD HH:mm")
    );
    const data = filteredChartData.map((item) => item.value);

    return {
      labels,
      datasets: [
        {
          label: "Value",
          data,
          fill: true,
          tension: 0.3,
          borderWidth: 2,
          pointRadius: 4,
          segment: {
            borderColor: (ctx) => {
              if (ctx.p0.parsed.y < 0) {
                return "rgb(255, 99, 132)"; // Red line
              }
              return "#6374C3"; // Blue line
            },
          },
          // ✨ NEW: Updated backgroundColor function for individual gradients
          backgroundColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea, scales } = chart;

            // If chart not ready, skip rendering gradient
            if (!chartArea || !scales?.y) return null;

            const { top, bottom } = chartArea;
            const y0 = scales.y.getPixelForValue(0);

            // Ensure valid numeric values
            if (
              !isFinite(y0) ||
              !isFinite(top) ||
              !isFinite(bottom) ||
              bottom - top === 0
            ) {
              return "rgba(99, 116, 195, 0.3)"; // fallback color
            }

            const gradient = ctx.createLinearGradient(0, top, 0, bottom);
            const stop = (y0 - top) / (bottom - top);
            const clampedStop = Math.max(0, Math.min(1, stop));

            gradient.addColorStop(0, "rgba(99, 116, 195, 0.3)");
            gradient.addColorStop(clampedStop, "rgba(99, 116, 195, 0)");
            gradient.addColorStop(clampedStop, "rgba(255, 99, 132, 0)");
            gradient.addColorStop(1, "rgba(255, 99, 132, 0.3)");

            return gradient;
          },
        },
      ],
    };
  }, [filteredChartData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: false },
      zoom: {
        pan: { enabled: true, mode: "x" },
        zoom: {
          wheel: { enabled: true, sensitivity: 0.5 },
          drag: {
            enabled: true,
            borderColor: "rgba(0,0,0,0.3)",
            borderWidth: 1,
            backgroundColor: "rgba(0,0,0,0.1)",
          },
          mode: "x",
          speed: 0.1,
          limits: { x: { minRange: 2 } },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true, // pastikan mulai dari 0
        min: Math.min(0, minY), // kalau ada nilai negatif → extend kebawah
        max: maxY,
        ticks: {
          callback: (value) => {
            const absVal = Math.abs(value);
            const formatted =
              absVal >= 1000 ? `${(absVal / 1000).toFixed(0)}K` : `${absVal}`;
            return value < 0 ? `-${formatted}` : formatted;
          },
        },
        grid: { display: true },
      },
      x: { grid: { display: false } },
    },
  };

  return (
    <div className="w-full h-full p-4">
      <Line ref={chartRef} data={chartData} options={options} />
    </div>
  );
};
